import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [manualMode, setManualMode] = useState(false);
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const requestCode = async () => {
    if (!email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      setBusy(true);
      const r = await api.post("/auth/otp/request", { email: email.trim().toLowerCase() });
      if (r.data?.needs_access_request) {
        toast.message("This email isn't approved for Half Job yet.");
        navigate(`/request-access?email=${encodeURIComponent(email.trim().toLowerCase())}`);
        return;
      }
      const isManual = !!r.data?.manual_mode;
      setManualMode(isManual);
      setStep("otp");
      setResendIn(isManual ? 0 : 30);
      if (!isManual) {
        const t = setInterval(() => {
          setResendIn((n) => {
            if (n <= 1) { clearInterval(t); return 0; }
            return n - 1;
          });
        }, 1000);
        if (r.data?.sent) {
          toast.success("Code sent. Check your inbox.");
        } else {
          toast.info("Code generated. If you don't see an email, ask your admin to send you one.");
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not continue");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (codeVal) => {
    const c = (codeVal ?? code).trim();
    if (c.length !== 6) return;
    try {
      setBusy(true);
      const r = await api.post("/auth/otp/verify", { email, code: c });
      signIn(r.data.token, r.data.user);
      toast.success("Welcome to Half Job");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That code didn't work — check it and try again.");
      // Preserve the entered digits so the user can fix a single typo without re-typing all six.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-kraft relative overflow-hidden"
    >
      {/* Workshop background image */}
      <div
        className="absolute inset-0 opacity-25 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/d521eede-4a6a-4a03-92ae-1c7d49920d93/images/bd7e8813174af2b61844c7a82c5ee0a923880e4bb2e99553afb55190a05c2908.png')",
        }}
        aria-hidden
      />
      <div className="relative z-10 min-h-screen flex flex-col px-6 py-10">
        <div className="flex items-center justify-between">
          <Logo size={24} />
        </div>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md tactile-card p-6">
            {step === "email" ? (
              <>
                <p className="label-cap">Sign in</p>
                <h1 className="font-heading text-3xl font-bold text-timber-900 mt-1 leading-tight">
                  Finish what you started.
                </h1>
                <p className="text-timber-700 mt-2">
                  {`Enter your email and we'll send you a 6-digit code.`}
                </p>
                <div className="mt-6 space-y-3">
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && requestCode()}
                    placeholder="you@home.com"
                    className="min-h-[56px] bg-chalk border-2 border-stone_border rounded-xl text-lg"
                  />
                  <Button
                    data-testid="login-send-code-btn"
                    onClick={requestCode}
                    disabled={busy}
                    className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] text-base font-bold btn-press"
                  >
                    {busy ? "Sending…" : "Send code"}
                  </Button>
                </div>
                <p className="text-xs text-timber-500 mt-6">
                  Half Job is in private beta. New here?{" "}
                  <button
                    onClick={() => navigate(`/request-access${email ? `?email=${encodeURIComponent(email)}` : ""}`)}
                    data-testid="login-request-access-link"
                    className="text-masking font-bold underline"
                  >
                    Request access
                  </button>
                </p>
                {/* Path B — admin-issued sign-in code. The code lives in the same
                    otp_codes collection as self-serve codes, but admins hand it
                    out-of-band (SMS/Signal/in person). This link routes to the
                    redeem screen so the user doesn't accidentally generate a
                    fresh OTP and invalidate the admin code. */}
                <button
                  onClick={() => navigate(`/code${email ? `?email=${encodeURIComponent(email)}` : ""}`)}
                  data-testid="login-have-code-link"
                  className="w-full text-center text-sm text-timber-700 mt-4 font-semibold underline"
                >
                  Have a code from a Half Job admin?
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setStep("email")}
                  data-testid="login-back-btn"
                  className="text-timber-700 flex items-center gap-1 text-sm font-semibold"
                >
                  <ArrowLeft size={16} /> Change email
                </button>
                <h1 className="font-heading text-3xl font-bold text-timber-900 mt-3 leading-tight">
                  {manualMode ? "Ask your admin." : "Check your email."}
                </h1>
                <p className="text-timber-700 mt-2">
                  {manualMode ? (
                    <>
                      Half Job is in private beta with manual onboarding. Your admin will give you
                      a 6-digit sign-in code for <span className="font-bold">{email}</span>. Enter
                      it below.
                    </>
                  ) : (
                    <>
                      We sent a code to <span className="font-bold">{email}</span>.
                    </>
                  )}
                </p>
                <div className="mt-6 flex justify-center" data-testid="otp-wrapper">
                  <InputOTP
                    maxLength={6}
                    value={code}
                    onChange={(v) => {
                      setCode(v);
                      if (v.length === 6) verify(v);
                    }}
                  >
                    <InputOTPGroup>
                      {[0,1,2,3,4,5].map(i => (
                        <InputOTPSlot
                          key={i}
                          index={i}
                          data-testid={`otp-slot-${i}`}
                          className="w-12 h-14 text-2xl font-bold border-2 border-stone_border bg-chalk"
                        />
                      ))}
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                <Button
                  data-testid="login-verify-btn"
                  onClick={() => verify()}
                  disabled={busy || code.length !== 6}
                  className="w-full mt-6 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[56px] text-base font-bold btn-press"
                >
                  {busy ? "Checking…" : "Verify"}
                </Button>
                <button
                  data-testid="login-resend-btn"
                  onClick={requestCode}
                  disabled={resendIn > 0 || manualMode}
                  className="w-full text-center text-sm text-timber-700 mt-4 font-semibold disabled:opacity-50"
                >
                  {manualMode
                    ? "Ask your admin to issue a new code if needed"
                    : resendIn > 0
                      ? `Resend in ${resendIn}s`
                      : "Resend code"}
                </button>
              </>
            )}
          </div>
        </div>
        <p className="text-center text-xs text-timber-500 mt-6">
          A DIY copilot that gets the job done · v1
        </p>
        <p className="text-center text-[11px] text-timber-500 mt-2">
          <a href="/terms" data-testid="login-terms-link" className="underline hover:text-timber-700">
            Terms &amp; Acceptable Use
          </a>
        </p>
      </div>
    </div>
  );
}
